// Ajustements manuels de la place sélectionnée (D-301, D-302, D-304) : tourner, redimensionner,
// scinder, fusionner avec une voisine, retirer du plan, renuméroter ; historique des changements.
// Le déplacement se fait par un clic sur la carte (plan géographique). Chaque action envoie la
// version connue : une modification concurrente est refusée et la fiche est rechargée.
import { Bandeau, Bouton, Champ } from '@cc/ui';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { date } from '../../commun/format';
import { INVALIDER_MARCHES } from './api';
import {
  ajusterPlace,
  fusionnerPlaces,
  renumeroterPlace,
  retirerPlace,
  scinderPlace,
  useHistoriquePlace,
} from './plan-api';
import type { Emplacement } from './types';
import styles from './marches.module.css';

const LIBELLES_HISTORIQUE: Record<string, string> = {
  renumerotation: 'Renumérotation',
  fusion: 'Fusion',
  scission: 'Scission',
  ajout: 'Ajout',
  retrait: 'Retrait',
  ajustement: 'Ajustement',
  recalage: 'Recalage de l’îlot',
  import: 'Import',
  decoupage: 'Découpage',
};

export function AjustementsPlace({
  place,
  emplacements,
}: {
  place: Emplacement;
  emplacements: Emplacement[];
}) {
  const version = place.position_version ?? 0;
  const [angle, setAngle] = useState(15);
  const [largeur, setLargeur] = useState(2);
  const [profondeur, setProfondeur] = useState(2);
  const [parts, setParts] = useState(2);
  const [voisine, setVoisine] = useState('');
  const [reference, setReference] = useState(place.reference);
  const [motif, setMotif] = useState('');
  const historique = useHistoriquePlace(place.id);
  const invalider = [...INVALIDER_MARCHES, ['marches', 'historique', place.id]];
  const autre = emplacements.find(
    (e) => e.reference.toUpperCase() === voisine.trim().toUpperCase() && e.id !== place.id,
  );
  const tourner = useAction({
    executer: () =>
      ajusterPlace(place.id, { action: 'tourner', angle_deg: angle, version_attendue: version }),
    succes: 'Place tournée.',
    invalider,
  });
  const dimensions = useAction({
    executer: () =>
      ajusterPlace(place.id, {
        action: 'redimensionner',
        largeur_m: largeur,
        profondeur_m: profondeur,
        version_attendue: version,
      }),
    succes: 'Dimensions enregistrées.',
    invalider,
  });
  const scission = useAction({
    executer: () => scinderPlace(place.id, { parts, version_attendue: version }),
    succes: (r) => `Place scindée : ${r.creees.map((c) => c.reference).join(', ')} créée(s).`,
    invalider,
  });
  const fusion = useAction({
    executer: () =>
      fusionnerPlaces([
        { id: place.id, version_attendue: version },
        { id: autre!.id, version_attendue: autre!.position_version ?? 0 },
      ]),
    succes: 'Places fusionnées ; la voisine est retirée du plan.',
    invalider,
  });
  const retrait = useAction({
    executer: () => retirerPlace(place.id, { motif, version_attendue: version }),
    succes: 'Place retirée du plan ; son numéro reste dans l’historique.',
    invalider,
  });
  const renum = useAction({
    executer: () => renumeroterPlace(place.id, { reference, motif, version_attendue: version }),
    succes: 'Numéro changé ; l’ancien reste dans l’historique.',
    invalider,
  });
  const erreur = [tourner, dimensions, scission, fusion, retrait, renum].find(
    (a) => a.messageErreur,
  )?.messageErreur;
  const occupe = Boolean(place.attribution_en_cours);
  return (
    <section className={styles.pile} aria-label={`Ajuster la place ${place.reference}`}>
      <h3>Ajuster la place {place.reference}</h3>
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      <div className={styles.barreFiltres}>
        <Champ
          libelle="Angle (degrés)"
          type="number"
          min={-360}
          max={360}
          value={angle}
          onChange={(e) => setAngle(Number(e.target.value))}
        />
        <Bouton
          variante="secondaire"
          disabled={tourner.enCours}
          onClick={() => tourner.lancer(undefined)}
        >
          Tourner
        </Bouton>
      </div>
      <div className={styles.barreFiltres}>
        <Champ
          libelle="Largeur (m)"
          type="number"
          min={0.5}
          max={50}
          step={0.1}
          value={largeur}
          onChange={(e) => setLargeur(Number(e.target.value))}
        />
        <Champ
          libelle="Profondeur (m)"
          type="number"
          min={0.5}
          max={50}
          step={0.1}
          value={profondeur}
          onChange={(e) => setProfondeur(Number(e.target.value))}
        />
        <Bouton
          variante="secondaire"
          disabled={dimensions.enCours}
          onClick={() => dimensions.lancer(undefined)}
        >
          Élargir ou réduire
        </Bouton>
      </div>
      <div className={styles.barreFiltres}>
        <Champ
          libelle="Nombre de parts"
          type="number"
          min={2}
          max={4}
          value={parts}
          onChange={(e) => setParts(Number(e.target.value))}
        />
        <Bouton
          variante="secondaire"
          disabled={occupe || scission.enCours}
          onClick={() => scission.lancer(undefined)}
        >
          Scinder
        </Bouton>
      </div>
      <div className={styles.barreFiltres}>
        <Champ
          libelle="Fusionner avec la place"
          value={voisine}
          onChange={(e) => setVoisine(e.target.value)}
          maxLength={30}
        />
        <Bouton
          variante="secondaire"
          disabled={!autre || Boolean(autre.attribution_en_cours) || fusion.enCours}
          onClick={() => fusion.lancer(undefined)}
        >
          Fusionner
        </Bouton>
      </div>
      <Champ
        libelle="Motif (retrait ou renumérotation)"
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
        maxLength={300}
      />
      <div className={styles.barreFiltres}>
        <Champ
          libelle="Nouveau numéro"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          maxLength={30}
        />
        <Bouton
          variante="secondaire"
          disabled={motif.trim().length < 3 || reference === place.reference || renum.enCours}
          onClick={() => renum.lancer(undefined)}
        >
          Renuméroter
        </Bouton>
        <Bouton
          variante="discret"
          disabled={occupe || motif.trim().length < 3 || retrait.enCours}
          onClick={() => retrait.lancer(undefined)}
        >
          Retirer du plan
        </Bouton>
      </div>
      {occupe ? (
        <p className={styles.aide}>
          Place attribuée : elle ne peut être ni scindée, ni retirée, ni absorbée par une fusion.
        </p>
      ) : null}
      <h4>Historique de la place</h4>
      {historique.data?.length ? (
        <ul className={styles.aide}>
          {historique.data.map((h) => (
            <li key={h.id}>
              {date(h.cree_le)} · {LIBELLES_HISTORIQUE[h.action] ?? h.action}
              {h.reference_avant || h.reference_apres
                ? ` : ${h.reference_avant ?? ''} → ${h.reference_apres ?? ''}`
                : ''}
              {h.motif ? ` (${h.motif})` : ''}
              {h.auteur ? `, ${h.auteur}` : ''}
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.aide}>Aucun changement enregistré.</p>
      )}
    </section>
  );
}
